import * as yup from 'yup';
import { error } from '../../validation/text';
import { withdrawList } from '../../constants/Enum';

const passwordMixed =
  /(?=.*[a-zA-Z])(?=.*[0-9])(?=.*[!-/:-@[-`{-~])[a-zA-z0-9!-/:-@[-`{-~]|(?=.*[a-zA-Z])(?=.*[0-9])[a-zA-z0-9]|(?=.*[a-zA-Z])(?=.*[!-/:-@[-`{-~])[a-zA-z!-/:-@[-`{-~]|(?=.*[0-9])(?=.*[!-/:-@[-`{-~])[0-9!-/:-@[-`{-~]{6,}/;

const companyValidation = yup
  .string()
  .required(error.required)
  .max(50, error.below50text);

const companyKanaValidation = yup
  .string()
  .max(250, error.below250)
  .required(error.required)
  .test('is-katakana-and-number', error.kana, function (value) {
    return !!value.match(/^[ァ-ヶー　0-9]*$/);
  });

const companyShortValidation = yup
  .string()
  .max(250, error.below250)
  .required(error.required)
  .test('is-short', error.excludeLtd, (value) => {
    const invalidPatterns = [
      /株式会社/,
      /(株)/,
      /（株）/,
      /カ）/,
      /カ\)/,
      /ｶ\)/
    ];
    return !invalidPatterns.some((pattern) => pattern.test(value));
  });

const emailValidation = yup
  .string()
  .lowercase()
  .required(error.required)
  .email(error.email)
  .max(250, error.below250);

const newEmailValidation = yup
  .string()
  .lowercase()
  .required(error.required)
  .email(error.email)
  .max(250, error.below250)
  .test('unique-email', error.uniqueEmail, function (value) {
    const prevEmail: string = this.parent.prevEmail;
    return value !== prevEmail;
  });

const passwordValidation = yup
  .string()
  .required(error.required)
  .min(6, error.min)
  .max(250, error.below250)
  .matches(passwordMixed, error.mixed);

const passwordConfirmValidation = yup
  .string()
  .required(error.required)
  .test('passwordMatch', error.passwordMatch, function (value) {
    if (value && value !== this.resolve(yup.ref('password'))) {
      return false;
    }
    return true;
  });

const requiredValidation = yup
  .string()
  .required(error.required)
  .notOneOf(['選択してください'], error.required);

const requiredMax250Validation = yup
  .string()
  .max(250, error.below250)
  .required(error.required);

const requiredMax250KanaValidation = yup
  .string()
  .max(250, error.below250)
  .required(error.required)
  .test('is-katakana', error.nameKana, function (value) {
    return !!value.match(/^[ァ-ヶー　]*$/);
  });

const telValidation = yup
  .string()
  .matches(/^[0-9-]*$/, error.tel)
  .max(20, error.below20)
  .required(error.required);

const urlValidation = yup
  .string()
  .url(error.url)
  .max(250, error.below250)
  .nullable();

const termsValidation = yup.boolean().oneOf([true], error.terms).required();

export const emailChangeSchema = yup.object().shape({
  prevEmail: emailValidation,
  newEmail: newEmailValidation
});

export const userLoginSchema = yup.object().shape({
  email: emailValidation,
  password: passwordValidation
});

export const userPasswordResetInputSchema = yup.object().shape({
  password: passwordValidation,
  passwordConfirm: passwordConfirmValidation
});

export const userPasswordResetSchema = yup.object().shape({
  email: emailValidation
});

export const userRegisterSchema = yup.object().shape({
  email: emailValidation,
  privacyCheck: yup.bool().oneOf([true], error.requiredSelect)
});

export const userRegisterInfoSchema = yup.object().shape({
  company: companyValidation,
  companyKana: companyKanaValidation,
  companyShort: companyShortValidation,
  department: requiredMax250Validation,
  email: emailValidation,
  firstName: requiredMax250Validation,
  firstNameKana: requiredMax250KanaValidation,
  homepage: urlValidation,
  industry: requiredValidation,
  lastName: requiredMax250Validation,
  lastNameKana: requiredMax250KanaValidation,
  password: passwordValidation,
  passwordConfirm: passwordConfirmValidation,
  tel: telValidation
});

export const accountBasicChangeSchema = yup.object().shape({
  company: companyValidation,
  companyKana: companyKanaValidation,
  companyShort: companyShortValidation,
  department: requiredMax250Validation,
  firstName: requiredMax250Validation,
  firstNameKana: requiredMax250KanaValidation,
  homepage: urlValidation,
  industry: requiredValidation,
  lastName: requiredMax250Validation,
  lastNameKana: requiredMax250KanaValidation,
  tel: telValidation,
  certificationFlg: yup.number().nullable(),
  certificationName: yup.string().when('certificationFlg', {
    is: (value: number) => value === 1,
    then: (schema) => schema.required(error.required).max(250, error.below250),
    otherwise: (schema) => schema.nullable()
  })
});

export const accountWithdrawalSchema = yup.object().shape({
  reasonSelectedOption: yup
    .number()
    .required()
    .notOneOf([0], error.requiredSelect),
  reasonDetail: yup
    .string()
    .when('reasonSelectedOption', {
      is: withdrawList.other,
      then: (schema) => schema.required(error.required)
    })
    .max(2000, error.below2000)
});

export const memberRegisterInfoSchema = yup.object().shape({
  firstName: requiredMax250Validation,
  firstNameKana: requiredMax250KanaValidation,
  lastName: requiredMax250Validation,
  lastNameKana: requiredMax250KanaValidation,
  password: passwordValidation,
  passwordConfirm: passwordConfirmValidation,
  terms: termsValidation
});

export const memberInviteSchema = yup.object().shape({
  email: emailValidation
});
